import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import DropdownViews from 'ps-components/Dropdown/DropdownViews';
import apiClient from 'ps-components/lib/apiClient';
import { SLIDEOUTS } from "../Slideout/Slideouts";
import { showSlideout } from 'ps-components/Slideout/SlideoutReducer';
import { useMasterAccountId } from "../../hooks/redux";
import addAlert from 'ps-components/AlertToast/addAlert';

type ViewSelectorProps = {
  pageCode: string;
  categoryCode: string;
  pageName: string;
  page?: string;
  label?: string;
  elementCountLimit?: number;
  showAggregates?: boolean;
  includeAggregates?: boolean;
  elementsFilter?: (element: any, index: number, arr: any[]) => boolean;
  categoryFilter?: (category: any, index: number, arr: any[]) => boolean;
  onChange?: () => void;
  hideRequiredFields?: boolean;
  dropdownClass?: string;
};

type View = {
  view_pk: number;
  view_name: string;
}

export default function ViewSelector(props: ViewSelectorProps) {
  const { pageCode, categoryCode, page, label, pageName, elementCountLimit, showAggregates = false, 
    includeAggregates = false, elementsFilter, categoryFilter, onChange, hideRequiredFields = false, dropdownClass } = props;
  const dispatch = useDispatch();
  const userRights = useSelector((state: any) => state.session.user.rights);
  const slideoutType = useSelector((state: any) => state.slideout.slideoutType);
  const [views, setViews] = useState<View[]>([]);
  const [defaultViewId, setDefaultViewId] = useState<number | null>(null);
  const [selectedViewPk, setSelectedViewPk] = useState<number | null>(null);
  const maid = useMasterAccountId();

  const getViewName = () => {
    const selectedView = views?.find((view: View) => view.view_pk === selectedViewPk);
    return selectedView?.view_name || null;
  }

  /* GET VIEWS */
  const getViews = async () => {
    try {
      const { data } = await apiClient.apiGet(`/cms/api/accounts/${maid}/views`, {
        params: {
          contextName: pageCode,
          portAll: true,
          isFacet: false
        }
      });
      const views = data?.data?.views;
      const defaultViewId = data?.data?.defaultViewId;
      if (views) setViews(views);
      if (defaultViewId) setDefaultViewId(defaultViewId);
    } catch (e) {
      console.log('Error getting views for View Selector', e);
      addAlert('Error getting views', 'danger');
    }
  }

  /* GET SELECTED VIEW */
  const getSelectedView = async () => {
    try {
      const { data } = await apiClient.apiGet(`/cms/api/accounts/${maid}/views/last-viewed`, {
        params: { contextName: pageCode }
      });
      const view_pk = data?.data?.view_pk;
      if (view_pk) {
        setSelectedViewPk(view_pk);
      }
    } catch (e) {
      console.log('Error getting current for View Selector', e);
      addAlert('Error getting current view', 'danger');
    }
  }

  /* SET SELECTED VIEW */
  const setSelectedView = async (viewName: string) => {
    try {
      const selectedView = views?.find((surveyView: View) => surveyView.view_name === viewName);
      if (selectedView) {
        const { data } = await apiClient.apiPost(`/cms/api/accounts/${maid}/views/last-viewed`, {
          contextName: pageCode, viewId: selectedView.view_pk
        })
        if (data?.data?.success) {
          setSelectedViewPk(selectedView.view_pk);
          onChange && onChange();
        }
      }
    } catch (e) {
      console.log('Catch Error, Set Survey Selected View: ', e);
      addAlert('Error selecting new view', 'danger');
    }
  }

  useEffect(() => {
    getViews();
    getSelectedView();
  }, [])

  useEffect(() => {
    if (slideoutType === SLIDEOUTS.CONFIGURE_VIEW) {
      dispatch({ type: 'UPDATE_SLIDEOUT', slideoutProps: { views, selectedViewPk, defaultViewId } });
    }
  }, [views, selectedViewPk, defaultViewId])


  return (
    <div className="dropdown-group">
      <label>{label}</label>
      <Dropdown
        className="pricing-dropdown"
        dropdownClass={dropdownClass}
        text={getViewName()}
        customRenderComponent={DropdownViews}
        customRenderProps={{
          views: views,
          selectedViewPk: selectedViewPk,
          userRights: userRights,
          groupAdminCanConfigureViews: true,
          regularUserCanConfigureViews: true,
          onEdit: () => {

            dispatch(showSlideout(SLIDEOUTS.CONFIGURE_VIEW, {
              onRefresh: () => {
                console.log('View Selector: onRefresh views');
                getViews();
                getSelectedView();
              },
              categoryCode,
              page,
              pageCode,
              pageName,
              onRefreshData: () => {
                console.log('View Selector: handle view change');
                onChange && onChange();
              },
              includeAggregates,
              showAggregates,
              selectedViewPk,
              defaultViewId,
              views,
              useOverrideLabelFormatter: true,
              isGroupingEnabled: false,
              elementsFilter,
              categoryFilter,
              elementCountLimit,
              hideRequiredFields
            }));
          },
          onSelectView: (viewName: string) => setSelectedView(viewName),
        }}
      />
    </div>
  );
}
